import { match } from 'ts-pattern';
import {
  debounceTime,
  filter,
  groupBy,
  merge,
  mergeMap,
  Observable,
} from 'rxjs';
import { Option } from '@sapphire/result';
import { Nullable } from '@ps/utils';

import {
  ADVANCED_FILTER_TYPE,
  AdvancedFeasibilityFilters,
  ComputeFeasibilityParams,
  ComputeFeasibilityParamsForPreDraft,
  SupplierAllocationsEditableContext,
} from '@ps/survey-utils';

import {
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from '../../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationChildrenActions,
  SurveyCreationChildrenApiActions,
} from '../../audience/survey-creation-audience.actions';
import { SurveyCreationInsightsActions } from '../survey-creation-insights.actions';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import {
  SurveyChildRegistryState,
  SurveyCreationChildAudienceState,
} from '../../survey-creation-state.model';
import {
  DEFAULT_CHILD_AUDIENCE_STATE,
  DEFAULT_CORE_CHILD_STATE,
} from '../../survey-creation-state.constants';
import {
  resolveScopedChildKeys,
  SurveyChildScope,
} from '../../survey-creation-child-registry.helper';
import { InsightsChildState } from '../survey-creation-insights-state.model';
import {
  RecalculationData,
  RecalculationTrigger,
} from './survey-creation-recalculation.types';

// Pending whenever Score/Feasibility is about to recalculate, or is blocked
// by manual processing (stays pending until Save or the toggle flips off).
export function shouldMarkAsPending(action: RecalculationTrigger): boolean {
  return match(action)
    .with(
      { type: SurveyCreationAudienceActions.toggleManualProcessing.type },
      (toggled) => !toggled.enabled,
    )
    .otherwise(() => true);
}

// Defines which actions should trigger a recalculation of Score and Feasibility.
export function qualifiesForRecalculation(
  action: RecalculationTrigger,
  enableManualProcessing: boolean,
): boolean {
  return match(action)
    .with(
      { type: SurveyCreationAudienceActions.toggleManualProcessing.type },
      (toggled) => !toggled.enabled,
    )
    .with(
      {
        type: SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess.type,
      },
      { type: SurveyCreationInsightsActions.refreshAfterSave.type },
      {
        type: SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess.type,
      },
      {
        type: SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess.type,
      },
      () => true,
    )
    .otherwise(() => !enableManualProcessing);
}

// One-shot data arrivals, not rapid user input, so never debounced.
export function isLoadAction(action: RecalculationTrigger): boolean {
  return (
    action.type ===
      SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess.type ||
    action.type ===
      SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess.type
  );
}

// Loads pass straight through; edits are debounced per action and scope.
export function debounceByActionType<T extends RecalculationTrigger>(
  ms: number,
): (source: Observable<T>) => Observable<T> {
  return (source) =>
    merge(
      source.pipe(filter((action) => isLoadAction(action))),
      source.pipe(
        filter((action) => !isLoadAction(action)),
        groupBy((action: T) => debounceGroupKey(action)),
        mergeMap((sameTrigger$) => sameTrigger$.pipe(debounceTime(ms))),
      ),
    );
}

// Action type plus its scope, or the survey a CPI edit belongs to.
function debounceGroupKey(action: RecalculationTrigger): string {
  return match(action)
    .with(
      { type: SurveyCreationAudienceActions.updateMetadata.type },
      { type: SurveyCreationAudienceActions.updateQualifications.type },
      { type: SurveyCreationAudienceActions.updateQuotas.type },
      { type: SurveyCreationChildrenActions.patchChildMetadata.type },
      (scoped) => `${scoped.type}|${scopeGroupKey(scoped.scope)}`,
    )
    .with(
      {
        type: SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess.type,
      },
      ({ type, survey }) => `${type}|${survey.id}`,
    )
    .otherwise(({ type }) => type);
}

// Order-insensitive: the same keys listed differently match.
function scopeGroupKey(scope: SurveyChildScope): string {
  return scope.kind === 'keys'
    ? `keys:${[...scope.keys]
        .map(String)
        .sort((left, right) => left.localeCompare(right))
        .join(',')}`
    : scope.kind;
}

// A drawer CPI edit concerns only its own row; every other trigger is about
// the active survey.
export function targetsActiveSurvey(
  action: RecalculationTrigger,
  activeSurveyId: number,
): boolean {
  return match(action)
    .with(
      {
        type: SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess.type,
      },
      ({ survey }) => survey.id === activeSurveyId,
    )
    .otherwise(() => true);
}

// Which children a recalculation trigger targets, minus non-participating
// parents once a real family exists (see `resolveLoadedParticipant`).
export function resolveRecalculationKeys(
  action: RecalculationTrigger,
  activeChildKey: Nullable<SurveyChildKey>,
  coreChildren: Record<SurveyChildKey, SurveyChildRegistryState>,
  insightsChildren: Record<SurveyChildKey, InsightsChildState>,
): SurveyChildKey[] {
  const candidateKeys = match(action)
    .with(
      { type: SurveyCreationAudienceActions.updateMetadata.type },
      { type: SurveyCreationAudienceActions.updateQualifications.type },
      { type: SurveyCreationAudienceActions.updateQuotas.type },
      { type: SurveyCreationChildrenActions.patchChildMetadata.type },
      (scoped) =>
        // Core children carry their own `participant` flag.
        resolveScopedChildKeys(
          coreChildren,
          { activeChildKey, participantKeys: [] },
          scoped.scope,
        ),
    )
    // The active child arrives on its own loadActiveSurveyForEditSuccess.
    .with(
      {
        type: SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess.type,
      },
      () =>
        Object.keys(coreChildren).filter(
          (key) => String(key) !== String(activeChildKey),
        ),
    )
    .with(
      {
        type: SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess.type,
      },
      ({ survey }) => [survey.id],
    )
    // Also releases siblings left pending by manual processing.
    .with(
      { type: SurveyCreationInsightsActions.refreshAfterSave.type },
      { type: SurveyCreationAudienceActions.toggleManualProcessing.type },
      () => [
        ...activeKeys(activeChildKey),
        ...pendingRecalculationKeys(coreChildren, insightsChildren).filter(
          (key) => String(key) !== String(activeChildKey),
        ),
      ],
    )
    .otherwise(() => activeKeys(activeChildKey));

  const isMultiLocale = Object.keys(coreChildren).length > 1;

  return isMultiLocale
    ? candidateKeys.filter((key) => coreChildren[key]?.participant !== false)
    : candidateKeys;
}

function activeKeys(
  activeChildKey: Nullable<SurveyChildKey>,
): SurveyChildKey[] {
  return Option.from(activeChildKey)
    .map((key) => [key])
    .unwrapOr([]);
}

// Registered children still awaiting feasibility.
function pendingRecalculationKeys(
  coreChildren: Record<SurveyChildKey, SurveyChildRegistryState>,
  insightsChildren: Record<SurveyChildKey, InsightsChildState>,
): SurveyChildKey[] {
  return Object.keys(coreChildren).filter(
    (key) => !!insightsChildren[key]?.feasibility.loading,
  );
}

export interface RecalculationChildTask {
  key: SurveyChildKey;
  coreChild: SurveyChildRegistryState;
  audienceChild: SurveyCreationChildAudienceState;
}

// One task per resolved key, carrying each child's own slice of state —
// the unit of work the per-child feasibility pipeline runs on.
export function toRecalculationChildTasks(
  keys: SurveyChildKey[],
  coreChildren: Record<SurveyChildKey, SurveyChildRegistryState>,
  audienceChildren: Record<SurveyChildKey, SurveyCreationChildAudienceState>,
): RecalculationChildTask[] {
  return keys.map((key) => ({
    key,
    coreChild: coreChildren[key] ?? DEFAULT_CORE_CHILD_STATE,
    audienceChild: audienceChildren[key] ?? DEFAULT_CHILD_AUDIENCE_STATE,
  }));
}

export function toSupplierContext(
  data: RecalculationData,
): SupplierAllocationsEditableContext {
  return {
    loi: data.metadata.loi ?? 0,
    ir: data.metadata.incidence ?? 0,
    languageCode: data.survey.mapOr('', (survey) => survey.languageCode ?? ''),
    fieldTime: data.metadata.fieldTime ?? 0,
    goal: data.metadata.completes ?? 0,
    clickBalancing: data.metadata.clickBalancing,
  };
}

// Params for the feasibility request. Grouping only applies to an Exclude
// survey with exclusions enabled and at least one group — otherwise
// `isExclusion` still follows the committed filter type, just ungrouped.
export function computeFeasibilityParams(
  data: RecalculationData,
  advancedFilters: AdvancedFeasibilityFilters,
  supplierIds: number[],
  offerPrice: Option<number>,
): ComputeFeasibilityParams {
  const isExclusion = advancedFilters.type === ADVANCED_FILTER_TYPE.EXCLUDE;

  // PD-17726: `isExclusion` always follows the filter type; an ungrouped
  // request zeroes the grouping instead, not the type.
  const base: ComputeFeasibilityParams = {
    surveyId: data.surveyId,
    user: data.user,
    metadata: data.metadata,
    qualifications: data.qualifications,
    quotas: data.quotas,
    catalogQualifications: data.catalogQualifications,
    offerPrice,
    supplierIds,
    surveyGroupIds: [],
    isExclusion,
    groupingStatuses: [],
  };

  if (!isExclusion || !advancedFilters.excludeFromFeasibleAudience) {
    return base;
  }

  const allGroupIds = [
    ...new Set([
      ...advancedFilters.matchedGroupIds,
      ...advancedFilters.manualGroupIds,
    ]),
  ];

  if (!allGroupIds.length) {
    return base;
  }

  return {
    ...base,
    surveyGroupIds: allGroupIds,
    groupingStatuses: [...advancedFilters.respondentStatuses],
  };
}

export function computeFeasibilityParamsForPreDraft(
  user: ComputeFeasibilityParamsForPreDraft['user'],
  formData: Pick<
    ComputeFeasibilityParamsForPreDraft,
    'metadata' | 'qualifications' | 'quotas'
  >,
): ComputeFeasibilityParamsForPreDraft {
  return { user, ...formData };
}
